import React, { useRef, useEffect, useState, useCallback } from 'react';

function BuildingSvgWrapper(props) {
  const {
    svgContent,
    config,
    selectedElementId,
    onSelectElement,
    valueByElementId,
    svgElements,
    onHover,
    onHoverEnd,
  } = props;

  const containerRef = useRef(null);
  const svgContainerRef = useRef(null);
  const svgRefElement = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });

  const resolveRule = useCallback((row) => {
    if (!row) return null;

    const thresholdKey = config.thresholdKey || 'value';
    const value = row[thresholdKey];

    if (value == null) return null;

    for (const rule of config.thresholds) {
      if (rule.type === 'string') {
        // String exact match
        if (String(value).toLowerCase() === String(rule.matchValue || '').toLowerCase()) {
          return rule;
        }
      } else {
        // Numeric range match (default)
        const numValue = Number(value);
        if (Number.isNaN(numValue)) continue;

        const minOk = rule.min == null || numValue >= rule.min;
        const maxOk = rule.max == null || numValue < rule.max;
        if (minOk && maxOk) return rule;
      }
    }
    return null;
  }, [config.thresholds, config.thresholdKey]);

  const findElementId = useCallback((target) => {
    let current = target;
    let depth = 0;
    while (current && depth < 10) {
      if (current.id && svgElements.includes(current.id)) {
        return current.id;
      }
      current = current.parentElement;
      depth++;
    }
    return null;
  }, [svgElements]);

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setZoom((prev) => Math.min(Math.max(prev * delta, 0.1), 10));
    }
  };

  const handleMouseDown = (e) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      setIsPanning(true);
      setStartPan({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  const handleMouseMoveMain = (e) => {
    if (isPanning) {
      setOffset({
        x: e.clientX - startPan.x,
        y: e.clientY - startPan.y
      });
    }
  };

  const handleMouseUp = () => setIsPanning(false);

  // Effect 1: Initial SVG Rendering & Event Setup
  useEffect(() => {
    if (!svgContent || !svgContainerRef.current) return;

    try {
      svgContainerRef.current.innerHTML = '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svgContent;
      const svgEl = tempDiv.querySelector('svg');
      if (!svgEl) return;

      svgEl.setAttribute('width', '100%');
      svgEl.setAttribute('height', '100%');
      svgEl.style.overflow = 'visible';
      svgRefElement.current = svgEl;

      // Capture original styles once
      const allElements = svgEl.querySelectorAll('[id]');
      allElements.forEach((el) => {
        if (!el.getAttribute('data-orig-fill')) {
          el.setAttribute('data-orig-fill', el.style.fill || el.getAttribute('fill') || '');
        }
        if (!el.getAttribute('data-orig-stroke')) {
          el.setAttribute('data-orig-stroke', el.style.stroke || el.getAttribute('stroke') || '');
        }
        if (!el.getAttribute('data-orig-stroke-width')) {
          el.setAttribute('data-orig-stroke-width', el.style.strokeWidth || el.getAttribute('stroke-width') || '');
        }
        if (!el.getAttribute('data-orig-opacity')) {
          el.setAttribute('data-orig-opacity', el.style.fillOpacity || el.getAttribute('fill-opacity') || '');
        }
      });

      svgContainerRef.current.appendChild(svgEl);
    } catch (error) {
      console.error('Error rendering SVG:', error);
    }
  }, [svgContent]); // ONLY re-render when CONTENT changes

  // Effect 2: Update event listeners and basic styles that depend on panning
  useEffect(() => {
    const svgEl = svgRefElement.current;
    if (!svgEl) return;

    svgEl.style.cursor = isPanning ? 'grabbing' : 'default';

    const clickHandler = (e) => {
      if (isPanning) return;
      const elementId = findElementId(e.target);
      if (elementId) {
        onSelectElement(elementId);
      }
    };

    const moveHandler = (e) => {
      if (isPanning) return;
      const elementId = findElementId(e.target);
      if (elementId && svgElements.includes(elementId)) {
        onHover(elementId, {
          x: e.clientX,
          y: e.clientY,
          elementData: valueByElementId.get(elementId)
        });
      } else {
        onHoverEnd();
      }
    };

    svgEl.addEventListener('click', clickHandler, true);
    svgEl.addEventListener('mousemove', moveHandler);
    svgEl.addEventListener('mouseleave', onHoverEnd);

    return () => {
      svgEl.removeEventListener('click', clickHandler, true);
      svgEl.removeEventListener('mousemove', moveHandler);
      svgEl.removeEventListener('mouseleave', onHoverEnd);
    };
  }, [svgElements, onSelectElement, onHover, onHoverEnd, isPanning, findElementId, valueByElementId]);

  // Effect 3: Dynamic Styling (Colors, Animations, Selection)
  useEffect(() => {
    const svgEl = svgRefElement.current;
    if (!svgEl) return;

    const allElements = svgEl.querySelectorAll('[id]');
    allElements.forEach((el) => {
      const id = el.getAttribute('id');
      el.style.cursor = 'pointer';
      el.style.transition = 'fill 0.3s ease, stroke 0.3s ease, stroke-width 0.3s ease';

      const row = valueByElementId.get(id);
      const rule = resolveRule(row);
      const visualMode = config.visualMode || 'none';

      // Reset animations
      el.classList.remove('animate-pulse', 'animate-shake', 'animate-flash');

      // 1. Reset Base Styles (Restore from captured originals)
      el.style.fill = el.getAttribute('data-orig-fill') || '';
      el.style.fillOpacity = el.getAttribute('data-orig-opacity') || '';
      el.style.stroke = el.getAttribute('data-orig-stroke') || '';
      el.style.strokeWidth = el.getAttribute('data-orig-stroke-width') || '';

      // 2. Apply Mapping Colors based on Mode
      if (rule && visualMode !== 'none') {
        if (visualMode === 'fill') {
          el.style.fill = rule.color;
          el.style.fillOpacity = '0.7';
        } else if (visualMode === 'border') {
          el.style.stroke = rule.color;
          el.style.strokeWidth = '3px';
          el.style.vectorEffect = 'non-scaling-stroke'; // Better for zoom
        }

        // Apply Animations
        if (rule.animation && rule.animation !== 'none') {
          el.classList.add(`animate-${rule.animation}`);
        }
      }

      // 3. Selection Highlight (Overrides Mapping)
      if (id === selectedElementId) {
        el.style.stroke = '#000000';
        el.style.strokeWidth = visualMode === 'border' ? '5px' : '3px';
        el.style.fillOpacity = '1';
        el.style.filter = 'drop-shadow(0 0 4px rgba(0,0,0,0.5))';
        el.style.vectorEffect = 'non-scaling-stroke';
      } else if (visualMode !== 'border') {
        el.style.filter = 'none';
      }
    });

  }, [valueByElementId, config.thresholds, config.visualMode, selectedElementId, resolveRule]);

  // State for overlays
  const [overlays, setOverlays] = useState([]);

  // Calculate overlay positions
  // Calculate overlay positions
  React.useLayoutEffect(() => {
    if (!containerRef.current || !svgRefElement.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newOverlays = [];

    // Only show overlay for the selected element if it is mapped
    if (selectedElementId && valueByElementId.has(selectedElementId)) {
      const el = svgRefElement.current.getElementById(selectedElementId);
      if (el) {
        const rect = el.getBoundingClientRect();

        // Calculate position relative to container
        const x = rect.left - containerRect.left + containerRef.current.scrollLeft;
        const y = rect.top - containerRect.top + containerRef.current.scrollTop;
        const width = rect.width;

        // don't show if zero dimensions (invisible)
        if (width > 0 && rect.height > 0) {
          newOverlays.push({ id: selectedElementId, x, y, width });
        }
      }
    }

    setOverlays(newOverlays);
  }, [valueByElementId, zoom, offset, svgContent, props.onRemoveMapping, selectedElementId]); // Depend on dependencies that affect layout

  return (
    <div
      className="svg-wrapper"
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMoveMain}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{
        width: '100%',
        height: '90vh',
        overflow: 'auto',
        backgroundColor: config.backgroundColor || 'var(--color-background)',
        position: 'relative',
        cursor: isPanning ? 'grabbing' : 'auto',
      }}
    >
      <div
        ref={svgContainerRef}
        style={{
          width: '100%',
          height: '100%',
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          transition: isPanning ? 'none' : 'transform 0.1s ease-out',
        }}
      />

      <div style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', flexDirection: 'column', gap: '4px', zIndex: 10 }}>
        <button className="btn btn--secondary btn--sm" onClick={() => setZoom(z => z * 1.2)}>+</button>
        <button className="btn btn--secondary btn--sm" onClick={() => setZoom(z => z / 1.2)}>-</button>
        <button className="btn btn--secondary btn--sm" onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }}>⟲</button>
      </div>

      {/* Overlays for mapped elements */}
      {overlays.map(o => (
        <div
          key={o.id}
          style={{
            position: 'absolute',
            left: o.x + o.width - 8, // Shift slightly to right edge
            top: o.y - 8, // Shift slightly up
            width: '16px',
            height: '16px',
            background: 'red',
            color: 'white',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
            zIndex: 5,
            pointerEvents: 'auto'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (props.onRemoveMapping) props.onRemoveMapping(o.id);
          }}
          title={`Remove mapping for ${o.id}`}
        >
          ✕
        </div>
      ))}

      <div className="legend-overlay">
        <h4 style={{ fontSize: '10px', marginBottom: '8px', color: 'var(--color-text-secondary)' }}>THRESHOLDS ({config.visualMode?.toUpperCase()})</h4>
        {config.thresholds.map((t, i) => (
          <div key={i} className="legend-item">
            <div
              className="legend-color"
              style={{
                backgroundColor: config.visualMode === 'border' ? 'transparent' : t.color,
                border: config.visualMode === 'border' ? `2px solid ${t.color}` : 'none'
              }}
            />
            <span>{t.min ?? '-∞'} to {t.max ?? '∞'}</span>
            {t.animation && t.animation !== 'none' && <span style={{ opacity: 0.5, marginLeft: '4px' }}>({t.animation})</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default BuildingSvgWrapper;
